import React, { useEffect, useMemo, useState } from "react";
import { isEmpty } from "lodash";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { createStructuredSelector } from "reselect";

import {
    calculateMinZoneArea,
    detectCentralAirSystem,
    detectSingleZoneCoolingChanges,
    detectZoneChanges,
    getAccordionDataFromResults,
    getAccordionDataFromZone,
    getFieldInfo,
    getFieldLabel,
    getFieldLockNotice,
    getFlaggedZones,
    groupResultsByZone,
} from "utils/singleZoneCooling";
import { fetchSingleZoneCoolingResults, runSingleZoneCoolingCalculation } from "utils/singleZoneCooling/api";
import { useStateSelector } from "utils/field";
import { getBaseUnits, getUnitOptions } from "utils/fields";
import { getDecimalPlaces, getValidation } from "utils/fieldValidation";
import { formatArea, getMeasurementUnits } from "utils/displayMeasurements";
import { areArraysEqual } from "utils/index";
import { change } from "store/root/actions";

import Button from "components/Button";
import Input from "components/Input";
import Checkbox from "components/Input/Checkbox";
import InputRow from "components/Input/Row";
import ToggleMulti from "components/ToggleMulti";
import InputWithUnits from "components/Input/InputWithUnits";
import Loading from "components/Loading";
import Field from "components/Field";
import FixedBanner from "components/FixedBanner";
import Select from "components/Input/Select";
import ZoneAccordion from "./ZoneAccordion";
import ZoneForm from "./ZoneForm";
import ZoneResults from "./ZoneResults";
import CreateCoolingZone from "./CreateCoolingZone";
import { ZoneAccordionTooltips } from "./ZoneTooltips";

import classes from "./styles.module.scss";

const SingleZoneCooling = ({ change, modelSave }) => {
    const modelId = useStateSelector("model.modelId");
    const {
        coolingZones = {},
        ventilation = {},
        uiSettings = {},
        heatingCooling = {},
        location = {},
        singleZoneCooling = {},
    } = useStateSelector(`model.modelData`) || {};

    const { rooms: { bedrooms = 0 } = {} } = ventilation;
    const { primaryUnits = "Metric" } = uiSettings;
    const { cooling: { system: coolingSystem = {} } = {} } = heatingCooling || {};
    const { weather: { f280: { dcdbt: coolingDesignTemp = 0 } = {} } = {} } = location || {};
    const {
        occupants = bedrooms + 1,
        occupants_lock = true,
        outdoorDesignTemp = coolingDesignTemp,
        outdoorDesignTemp_lock = true,
        homeHasCentralAirSystem = false,
        latestAnalysisRunInfo = null,
    } = singleZoneCooling || {};

    const [mode, setMode] = useState("view");
    const [expandAll, setExpandAll] = useState(false);
    const [isRunning, setIsRunning] = useState(false);
    const [zoneResults, setZoneResults] = useState({});
    const [loadingResults, setLoadingResults] = useState(true);
    const [analysisFailed, setAnalysisFailed] = useState(false);
    const [designTempUnit, setDesignTempUnit] = useState("C");
    const [exportUnits, setExportUnits] = useState("");

    const displayUnits = useMemo(() => exportUnits || primaryUnits, [exportUnits, primaryUnits]);

    const {
        areaDisplayUnits = "m2",
        perimeterDisplayUnits = "m",
        tempDisplayUnits = "C",
        volumeDisplayUnits = "m3",
    } = getMeasurementUnits(displayUnits);

    const globalChangesDetected = useMemo(() => (
        detectSingleZoneCoolingChanges(singleZoneCooling, latestAnalysisRunInfo)
    ), [singleZoneCooling, latestAnalysisRunInfo]);

    const hasGlobalChanges = useMemo(() => (
        !isEmpty(globalChangesDetected) && Object.values(globalChangesDetected).some((value) => value)
    ), [globalChangesDetected]);

    // Determine whether a central air handling system exists in the model
    const centralAirSystemDetected = detectCentralAirSystem(coolingSystem);

    // Calculate the minimum area based on the number of occupants
    const minArea = useMemo(() => calculateMinZoneArea(occupants), [occupants]);

    // Initialize the single zone cooling data object when empty
    useEffect(() => {
        if (isEmpty(singleZoneCooling)) {
            change("model.modelData.singleZoneCooling", {
                occupants: bedrooms + 1,
                occupants_lock: true,
                outdoorDesignTemp: coolingDesignTemp,
                outdoorDesignTemp_lock: true,
                homeHasCentralAirSystem: centralAirSystemDetected,
                latestAnalysisRunInfo: null,
                exportUnits: "",
            });
        }
    }, []);

    // Update number of occupants using the default value as long as the field is locked
    useEffect(() => {
        if (occupants_lock) {
            change("model.modelData.singleZoneCooling.occupants", bedrooms + 1);
        }
    }, [bedrooms, occupants_lock]);

    // Update design temperature unit based on selected display units
    useEffect(() => {
        setDesignTempUnit(tempDisplayUnits);
    }, [displayUnits]);

    // Update outdoor design temperature using the default value as long as the field is locked
    useEffect(() => {
        if (outdoorDesignTemp_lock) {
            change("model.modelData.singleZoneCooling.outdoorDesignTemp", coolingDesignTemp);
        }
    }, [coolingDesignTemp, outdoorDesignTemp_lock]);

    // Update when a central air handling system is added or removed
    useEffect(() => {
        change("model.modelData.singleZoneCooling.homeHasCentralAirSystem", centralAirSystemDetected);
    }, [centralAirSystemDetected]);

    // Load results
    const updateZoneResults = (results) => {
        const resultsByZone = groupResultsByZone(results);
        setZoneResults(resultsByZone);
        setAnalysisFailed(isEmpty(resultsByZone));
    };

    useEffect(() => {
        if (!modelId) return;

        let cancelled = false;
        const loadResults = async () => {
            const results = await fetchSingleZoneCoolingResults(modelId);
            if (!cancelled && results) {
                console.log(results);
                updateZoneResults(results);
            }
            setLoadingResults(false);
        };

        loadResults();
        return () => {
            cancelled = true;
        };
    }, [modelId]);

    const numberOfZones = Object.keys(coolingZones).length;
    const numberOfResults = Object.keys(zoneResults).filter((zoneId) => !!coolingZones[zoneId]).length;

    const flaggedZones = useMemo(() => getFlaggedZones(coolingZones), [coolingZones]);

    // Save Model and Run analysis
    const runAnalysis = async () => {
        if (!modelId || numberOfZones === 0 || isRunning) return;

        setIsRunning(true);
        try {
            // Store latest analysis run info and save model
            change("model.modelData.singleZoneCooling.latestAnalysisRunInfo", {
                occupants,
                outdoorDesignTemp,
                homeHasCentralAirSystem,
                ...flaggedZones,
            });
            await modelSave?.();

            // Run analysis
            const response = await runSingleZoneCoolingCalculation({ modelId });
            if (response?.status === 200) {
                console.log(response.data);
                updateZoneResults(response.data);
            } else {
                console.log(response?.response?.data || response);
                setAnalysisFailed(true);
            }
        } finally {
            setIsRunning(false);
            setMode("view");
        }
    };

    // If global homeHasCentralAirSystem becomes false, reset systemServesZone for the necessary zones
    useEffect(() => {
        if (!homeHasCentralAirSystem) {
            Object.entries(coolingZones).forEach(([zoneId, zone]) => {
                if (zone.systemServesZone) {
                    change(`model.modelData.coolingZones.${zoneId}.systemServesZone`, false);
                }
            });
        }
    }, [homeHasCentralAirSystem, coolingZones]);

    return (
        <div className={classes.section}>
            <div className={classes.summaryHeader}>
                <div>
                    <h4>Single Zone Cooling</h4>
                    {modelId ? (
                        <nav className={classes.tabLinks} aria-label="Related model tabs">
                            <Link to={`/models/${modelId}/enclosure`} className={classes.tabLink}>
                                Enclosure
                            </Link>
                            <Link to={`/models/${modelId}/review`} className={classes.tabLink}>
                                Review
                            </Link>
                        </nav>
                    ) : null}
                </div>
                <div className={classes.headerActions}>
                    <CreateCoolingZone className={classes.whiteBtnIcon} change={change} displayUnits={displayUnits} />
                    <Button onClick={runAnalysis} disabled={isRunning || numberOfZones === 0} smallPadding>
                        {isRunning ? "Running..." : "Run Analysis"}
                    </Button>
                </div>
            </div>

            <div className={classes.inputRowContainer}>
                <InputRow gridTemplate="1fr 1fr 1fr">
                    <Field
                        component={InputWithUnits}
                        type="number"
                        name="model.modelData.singleZoneCooling.indoorDesignTemp"
                        label={getFieldLabel("indoorDesignTemp")}
                        input={{
                            value: 24,
                        }}
                        placeholder={0.0}
                        decimals={getDecimalPlaces("weatherLocation")}
                        disabled={true}
                        units={{
                            base: getBaseUnits("ashpTempCutoff", displayUnits),
                            options: getUnitOptions("temperature"),
                            selected: designTempUnit,
                            unitType: "temperature",
                        }}
                        onUnitChange={(unit) => setDesignTempUnit(unit)}
                    />
                    <Field
                        component={InputWithUnits}
                        type="number"
                        name="model.modelData.singleZoneCooling.outdoorDesignTemp"
                        label={getFieldLabel("outdoorDesignTemp")}
                        setValue={outdoorDesignTemp}
                        input={{
                            value: outdoorDesignTemp,
                        }}
                        placeholder={0.0}
                        decimals={getDecimalPlaces("weatherLocation")}
                        units={{
                            base: getBaseUnits("ashpTempCutoff", displayUnits),
                            options: getUnitOptions("temperature"),
                            selected: designTempUnit,
                            unitType: "temperature",
                        }}
                        disabled={outdoorDesignTemp_lock}
                        onUnitChange={(unit) => setDesignTempUnit(unit)}
                        lockNotice={getFieldLockNotice("outdoorDesignTemp")}
                        warning={globalChangesDetected?.outdoorDesignTemp && "singleZoneCoolingChangesDetected"}
                    />
                    <Field
                        component={Input}
                        type="number"
                        name="model.modelData.singleZoneCooling.occupants"
                        label={getFieldLabel("occupants")}
                        setValue={occupants}
                        input={{
                            value: occupants,
                        }}
                        validate={getValidation("ersNumOccupants")}
                        disabled={occupants_lock}
                        lockNotice={getFieldLockNotice("occupants")}
                        warning={globalChangesDetected?.occupants && "singleZoneCoolingChangesDetected"}
                    />
                </InputRow>
                <InputRow gridTemplate="1fr" lastRow>
                    <Field
                        className={classes.centralAirSystemCheckbox}
                        component={Checkbox}
                        name="model.modelData.singleZoneCooling.homeHasCentralAirSystem"
                        label={getFieldLabel("homeHasCentralAirSystem")}
                        input={{
                            value: centralAirSystemDetected,
                        }}
                        info={getFieldInfo("homeHasCentralAirSystem", centralAirSystemDetected)}
                        warning={globalChangesDetected?.homeHasCentralAirSystem && "singleZoneCoolingChangesDetected"}
                        large
                        disabled
                    />
                </InputRow>
            </div>
            {analysisFailed && (
                <FixedBanner className={classes.analysisFailedBanner} type="error">
                    <p>
                        Analysis failed. Please ensure the model has a status of
                        "Complete" under Room View in the Enclosure Tab and try again.
                    </p>
                </FixedBanner>
            )}
            <div className={classes.stickyHeader}>
                <InputRow className={classes.modeToggleRow}>
                    <div className={classes.leftContainer}>
                        <Field
                            component={ToggleMulti}
                            label="Mode"
                            className={classes.modeToggle}
                            active={mode}
                            options={[
                                { label: `View Results (${numberOfResults})`, id: "view", onClick: () => setMode("view") },
                                { label: `Edit Zones (${numberOfZones})`, id: "edit", onClick: () => setMode("edit") },
                            ]}
                        />
                        <div className={classes.unitsLabel}>Units: </div>
                        <Field
                            component={Select}
                            className={classes.exportUnitsSelect}
                            options={[
                                {
                                    value: "",
                                    label: "Same as Model",
                                },
                                {
                                    value: "Metric",
                                    label: "Metric (W, °C, m)",
                                },
                                {
                                    value: "Imperial",
                                    label: "Imperial (Btu/h, °F, ft)",
                                },
                            ]}
                            label="Units"
                            input={{
                                value: exportUnits,
                                onChange: (value) => setExportUnits(value),
                            }}
                            large
                            hideLabel
                        />
                    </div>
                    <div className={classes.rightContainer}>
                        <Field
                            component={ToggleMulti}
                            label="Expand All"
                            className={classes.modeToggle}
                            active={expandAll}
                            options={[
                                { label: "Collapse All", id: false, onClick: () => setExpandAll(!expandAll) },
                                { label: "Expand All", id: true, onClick: () => setExpandAll(!expandAll) },
                            ]}
                        />
                    </div>
                </InputRow>
                <div className={classes.coolingZonesHeader}>
                    <div className={classes.stat}>
                        <span>Status</span>
                    </div>
                    <div className={classes.stat}>
                        <span>Label</span>
                    </div>
                    <div className={classes.stat}>
                        <span>Rooms and Components</span>
                    </div>
                    <div className={classes.stat}>
                        <span>Measurements</span>
                    </div>
                </div>
            </div>
            {(isEmpty(coolingZones)) ? (
                <div className={classes.emptyState}>
                    <h3>No cooling zones to display</h3>
                    <p>Create a zone to get started.</p>
                </div>
            ) : loadingResults ? (
                <div className={classes.loadingResults}>
                    <Loading message="loading" />
                </div>
            ) : (
                <div className={classes.coolingZoneList}>
                    {Object.entries(coolingZones).sort((a, b) => a[0].localeCompare(b[0])).map(([zoneId, zone]) => {
                        const { label = "" } = zone;

                        const resultsWarnings = (latestAnalysisRunInfo?.zonesWarnings?.[zoneId] || []);
                        const resultsErrors = (latestAnalysisRunInfo?.zonesErrors?.[zoneId] || []);

                        const currentZoneWarnings = flaggedZones?.zonesWarnings?.[zoneId] || [];
                        const currentZoneErrors = flaggedZones?.zonesErrors?.[zoneId] || [];

                        const results = zoneResults[zoneId]?.data || {};

                        const hasResults = !isEmpty(results);

                        const changesDetected = hasResults && (
                            hasGlobalChanges ||
                            detectZoneChanges(zone, results) ||
                            !areArraysEqual(currentZoneWarnings, resultsWarnings) ||
                            !areArraysEqual(currentZoneErrors, resultsErrors)
                        );

                        const warnings = (mode === "edit" || !hasResults) ? currentZoneWarnings : resultsWarnings;
                        const errors = (mode === "edit" || !hasResults) ? currentZoneErrors : resultsErrors;
                        const minAreaToUse = (mode === "edit" || !hasResults) ? minArea : calculateMinZoneArea(latestAnalysisRunInfo?.occupants);
                        const formattedMinArea = formatArea(minAreaToUse, areaDisplayUnits);
                        const zoneData = (mode === "edit" || !hasResults)
                            ? getAccordionDataFromZone(zone, minAreaToUse, areaDisplayUnits, perimeterDisplayUnits)
                            : getAccordionDataFromResults(results, minAreaToUse, areaDisplayUnits, volumeDisplayUnits);

                        const hasWarnings = !isEmpty(warnings);
                        const hasErrors = !isEmpty(errors);

                        return (
                            <React.Fragment key={`zone-accordion-${zoneId}`}>
                                <ZoneAccordion
                                    id={zoneId}
                                    label={label}
                                    change={change}
                                    zoneData={zoneData}
                                    hasResults={hasResults}
                                    changesDetected={changesDetected}
                                    warnings={warnings}
                                    errors={errors}
                                    formattedMinArea={formattedMinArea}
                                    forceOpen={expandAll}
                                    isEditMode={mode === "edit"}
                                >
                                    {isRunning && (
                                        <div className={classes.loadingResults}>
                                            <Loading message="loading" />
                                        </div>
                                    )}
                                    {(!isRunning && mode === "edit") && (
                                        <ZoneForm
                                            zoneId={zoneId}
                                            accessor={`model.modelData.coolingZones.${zoneId}`}
                                            change={change}
                                            displayUnits={displayUnits}
                                            hasErrors={hasErrors}
                                            inZoneAccordion
                                        />
                                    )}
                                    {(!isRunning && mode === "view") && (
                                        <ZoneResults
                                            zoneId={zoneId}
                                            zoneData={zone}
                                            displayUnits={displayUnits}
                                            results={zoneResults[zoneId]}
                                            hasErrors={hasErrors}
                                            hasWarnings={hasWarnings}
                                            hasGlobalChanges={hasGlobalChanges}
                                        />
                                    )}
                                </ZoneAccordion>
                                <ZoneAccordionTooltips zoneId={zoneId} />
                            </React.Fragment>
                        )
                    })}
                </div>
            )}
        </div>
    );
};

const mapStateToProps = createStructuredSelector({});

const mapDispatchToProps = (dispatch) => ({
    change: (path, value) => dispatch(change(path, value)),
});

export default connect(mapStateToProps, mapDispatchToProps)(SingleZoneCooling);
